import { Button, Heading, VStack } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans } from '@lingui/react/macro';
import { EMPLOYER_BUSINESS_TYPES } from '@duke-hq/shared-constants/employer';
import {
  EmployerBusinessFieldsSchema,
  type EmployerBusinessFieldValues,
  type EmployerDetailsWritePayload,
} from '@duke-hq/svc-api/admin/trpc/routers/workPlacements/employers/schemas';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { EmployerBusinessFields } from '../../components/EmployerBusinessFields';
import { testIds } from '../../testIds';
import type { EmployerDetail } from '../types';

export type DetailsPayload = EmployerDetailsWritePayload;

export function DetailsEditForm({
  employer,
  onSave,
  onCancel,
  isSaving,
}: {
  employer: EmployerDetail;
  onSave: (payload: DetailsPayload) => void;
  onCancel: () => void;
  isSaving: boolean;
}): React.ReactElement {
  const currentType = employer.businessType ?? '';
  const isPreset =
    currentType === '' || EMPLOYER_BUSINESS_TYPES.some((type) => type === currentType);

  const methods = useForm<EmployerBusinessFieldValues>({
    resolver: zodResolver(EmployerBusinessFieldsSchema),
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: employer.name,
      abn: employer.abn ?? '',
      businessType: isPreset ? currentType : 'Other',
      businessTypeOther: isPreset ? '' : currentType,
    },
  });

  const onSubmit = (values: EmployerBusinessFieldValues) => {
    const businessType =
      values.businessType === 'Other' ? values.businessTypeOther.trim() : values.businessType;
    onSave({
      name: values.name,
      abn: values.abn.trim() === '' ? null : values.abn.trim(),
      businessType: businessType === '' ? null : businessType,
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        style={{ width: '100%', height: '100%' }}
        noValidate
      >
        <FormMainContainer>
          <FormContentContainer>
            <VStack align="stretch" spacing={4} w="full">
              <Heading fontSize="lg" size="xs" w="full">
                <Trans>Edit employer details</Trans>
              </Heading>
              <EmployerBusinessFields />
            </VStack>
          </FormContentContainer>

          <FormFooterContainer>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSaving}
              data-testid={testIds.saveEmployerButton}
            >
              <Trans>Save</Trans>
            </Button>
            <Button variant="secondary" onClick={onCancel} isDisabled={isSaving}>
              <Trans>Cancel</Trans>
            </Button>
          </FormFooterContainer>
        </FormMainContainer>
      </form>
    </FormProvider>
  );
}
